import moment from "moment";

/**
 * Кастомный компонент DatePicker
 */
export class DatePicker {
  constructor(el) {
    let index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    this.$input = el;
    this.$box = el.parentNode.parentNode;
    this.indexDatePicker = index;
    this.messages = options.messages || {};
    this.setDefaultDate = options.use_default;
    this.separator = this.$input.dataset.separator || ".";
    this.listeners = {}; // События: changeView, setDate

    // Инициализация DOM-контейнера
    this.$root = this.$box;
    this.$root.id = `root_datepicker-${index}`;

    // Настройка атрибутов
    this.$input.setAttribute("maxlength", 10);
    this.$input.setAttribute("autocomplete", "off");

    // Парсинг min/max дат
    this.minDate = this.parseDateAttribute("min");
    this.maxDate = this.parseDateAttribute("max");

    // Текущая и выбранная дата
    this.date = this.parseInputValue() || new Date();
    this.dateInput = new Date(this.date);

    // Приведение даты к допустимому диапазону
    this.clampDate();

    // UI состояние
    this.view = "day"; // day, month, year
    this.classShow = this.$input.dataset.class_show || "open";
    this.shiftYear = 0;

    // Локализованные названия
    this.weekDaysName = [this.messages.mo, this.messages.tu, this.messages.wd, this.messages.th, this.messages.fr, this.messages.st, this.messages.su];
    this.monthName = [this.messages.january, this.messages.february, this.messages.march, this.messages.april, this.messages.ma, this.messages.jun, this.messages.jul, this.messages.august, this.messages.september, this.messages.october, this.messages.november, this.messages.december];
    this.monthShortName = [this.messages.jan, this.messages.feb, this.messages.mar, this.messages.apr, this.messages.may, this.messages.june, this.messages.july, this.messages.aug, this.messages.sept, this.messages.oct, this.messages.nov, this.messages.dec];

    // Инициализация данных
    this.updateData(true);
  }

  /**
   * Парсит атрибут min/max в объект с датой и компонентами
   * @param {string} attr
   * @returns {Object|null}
   */
  parseDateAttribute(attr) {
    const value = this.$input.getAttribute(attr);
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    return {
      date,
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate() // исправлено: getDay() → getDate()
    };
  }

  /**
   * Парсит значение input в дату
   * @returns {Date|null}
   */
  parseInputValue() {
    const val = this.$input.value;
    if (!val) return null;
    const parts = val.split(this.separator);
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map(Number);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Ограничивает дату диапазоном min/max
   */
  clampDate() {
    if (this.minDate && this.date < this.minDate.date) {
      this.date = new Date(this.minDate.date);
    }
    if (this.maxDate && this.date > this.maxDate.date) {
      this.date = new Date(this.maxDate.date);
    }
  }

  /**
   * Генерация HTML компонента
   * @returns {string}
   */
  toHtml() {
    return `
      <i class="qform-field__block_box__field__view_pointer"></i>
      <nav class="qform-field__block_box__field__view_nav-top" id="navTop_${this.indexDatePicker}"></nav>
      <div class="qform-field__block_box__field__view_content">
        <div id="datepickerDay_${this.indexDatePicker}" class="datepicker__days datepicker__body"></div>
        <div id="datepickerMonths_${this.indexDatePicker}" class="datepicker__months datepicker__body"></div>
        <div id="datepickerYears_${this.indexDatePicker}" class="datepicker__years datepicker__body"></div>
      </div>
      <nav class="qform-field__block_box__field__view_nav-bottom">
        <div class="btn-prev" id="dpPrev_${this.indexDatePicker}"></div>
        <div class="btn-next" id="dpNext_${this.indexDatePicker}"></div>
      </nav>
    `;
  }

  /**
   * Обновляет внутренние данные по текущей дате
   * @param {boolean} skipRender
   */
  updateData() {
    let skipRender = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    this.dayCurrent = this.date.getDate();
    this.monthCurrent = this.date.getMonth();
    this.yearCurrent = this.date.getFullYear();
    this.maxDayCurrent = new Date(this.yearCurrent, this.monthCurrent + 1, 0).getDate();
    this.maxDayPrev = new Date(this.yearCurrent, this.monthCurrent, 0).getDate();
    this.weekDayStart = new Date(this.yearCurrent, this.monthCurrent, 1).getDay();
    this.weekDayEnd = new Date(this.yearCurrent, this.monthCurrent, this.maxDayCurrent).getDay();
    if (!skipRender) {
      this.dayToHtml();
      this.monthToHtml();
      this.yearToHtml();
    }
  }

  /**
   * Проверяет, разрешён ли выбор даты
   * @param {Date} date
   * @returns {boolean}
   */
  isDateDisabled(date) {
    return this.minDate?.date > date || this.maxDate?.date < date;
  }

  /**
   * Рендер дней месяца
   */
  dayToHtml() {
    const days = [];
    const prevDays = this.weekDayStart === 0 ? 6 : this.weekDayStart - 1;
    const nextDays = this.weekDayEnd === 0 ? 0 : 7 - this.weekDayEnd;

    // Предыдущий месяц
    for (let i = prevDays; i > 0; i--) {
      const prevMonth = this.monthCurrent === 0 ? 11 : this.monthCurrent - 1;
      const prevYear = this.monthCurrent === 0 ? this.yearCurrent - 1 : this.yearCurrent;
      const day = this.maxDayPrev - i + 1;
      const date = new Date(prevYear, prevMonth, day);
      const disabled = this.isDateDisabled(date) ? " -disabled-" : "";
      days.push({
        day,
        month: prevMonth,
        year: prevYear,
        class: "-other-month-" + disabled
      });
    }

    // Текущий месяц
    for (let i = 1; i <= this.maxDayCurrent; i++) {
      const date = new Date(this.yearCurrent, this.monthCurrent, i);
      const disabled = this.isDateDisabled(date) ? " -disabled-" : "";
      const isSelected = this.dateInput.getDate() === i && this.dateInput.getMonth() === this.monthCurrent && this.dateInput.getFullYear() === this.yearCurrent;
      const className = isSelected ? "-selected-" + disabled : disabled;
      days.push({
        day: i,
        month: this.monthCurrent,
        year: this.yearCurrent,
        class: className
      });
    }

    // Следующий месяц
    for (let i = 1; i <= nextDays; i++) {
      const nextMonth = this.monthCurrent === 11 ? 0 : this.monthCurrent + 1;
      const nextYear = this.monthCurrent === 11 ? this.yearCurrent + 1 : this.yearCurrent;
      const date = new Date(nextYear, nextMonth, i);
      const disabled = this.isDateDisabled(date) ? " -disabled-" : "";
      days.push({
        day: i,
        month: nextMonth,
        year: nextYear,
        class: "-other-month-" + disabled
      });
    }
    const weekDays = this.weekDaysName.map(day => `<div class="datepicker__day-name">${day}</div>`).join("");
    const cells = days.map(day => `
      <div
        class="datepicker__cell datepicker__cell-day ${day.class}"
        data-date="${day.day}"
        data-month="${day.month}"
        data-year="${day.year}">
        ${day.day}
      </div>
    `).join("");
    this.$dpDay.innerHTML = `
      <div class="datepicker__days-names">${weekDays}</div>
      <div class="datepicker__cells datepicker__cells-days">${cells}</div>
    `;
  }

  /**
   * Рендер месяцев
   */
  monthToHtml() {
    const cells = this.monthShortName.map((month, index) => {
      const isSelected = index === this.monthCurrent && this.dateInput.getFullYear() === this.yearCurrent;
      const disabled = this.isMonthDisabled(this.yearCurrent, index) ? " -disabled-" : "";
      return `
        <div
          class="datepicker__cell datepicker__cell-month ${isSelected ? "-selected-" : ""}${disabled}"
          data-month="${index}"
          data-year="${this.yearCurrent}">
          ${month}
        </div>
      `;
    }).join("");
    this.$dpMonth.innerHTML = `<div class="datepicker__cells datepicker__cells-months">${cells}</div>`;
  }

  /**
   * Проверка, доступен ли месяц
   * @param {number} year
   * @param {number} month
   * @returns {boolean}
   */
  isMonthDisabled(year, month) {
    if (this.minDate) {
      if (year < this.minDate.year) return true;
      if (year === this.minDate.year && month < this.minDate.month) return true;
    }
    if (this.maxDate) {
      if (year > this.maxDate.year) return true;
      if (year === this.maxDate.year && month > this.maxDate.month) return true;
    }
    return false;
  }

  /**
   * Рендер лет
   */
  yearToHtml() {
    const start = 8 * this.shiftYear - 4;
    const end = 8 * (this.shiftYear + 1);
    const cells = [];
    for (let i = start; i < end; i++) {
      const year = this.yearCurrent + i;
      const isSelected = year === this.dateInput.getFullYear();
      const disabled = this.isYearDisabled(year) ? " -disabled-" : "";
      cells.push(`
        <div
          class="datepicker__cell datepicker__cell-year ${isSelected ? "-selected-" : ""}${disabled}"
          data-year="${year}">
          ${year}
        </div>
      `);
    }
    this.$dpYear.innerHTML = `<div class="datepicker__cells datepicker__cells-years">${cells.join("")}</div>`;
  }

  /**
   * Проверка, доступен ли год
   * @param {number} year
   * @returns {boolean}
   */
  isYearDisabled(year) {
    return this.minDate?.date > year || this.maxDate?.date < year;
  }

  /**
   * Верхняя навигация
   */
  topNavToHtml() {
    const title = this.view === "day" ? `${this.monthName[this.monthCurrent]} ${this.yearCurrent}` : this.yearCurrent;
    this.$dpNavTop.innerHTML = `
      <div class="btn-title" id="setView_${this.indexDatePicker}">${title}</div>
      <div class="btn-now" id="setNowDate_${this.indexDatePicker}">
        ${this.view === "day" ? this.messages.today : this.messages.current}
      </div>
    `;
  }

  /**
   * Переключение вида (day → month → year)
   */
  setView() {
    switch (this.view) {
      case "day":
        this.view = "month";
        break;
      case "month":
        this.view = "year";
        break;
      default:
        this.view = "day";
        break;
    }
    this.emit("changeView");
  }

  /**
   * Выбор дня
   */
  setDay(e) {
    e.stopPropagation();
    const target = e.target.closest("[data-date]");
    if (!target || target.classList.contains("-disabled-")) return;
    const {
      date,
      month,
      year
    } = target.dataset;
    this.date = new Date(+year, +month, +date);
    this.emit("setDate");
    this.emit("changeView");
    this.$root.classList.remove(this.classShow);
  }

  /**
   * Выбор месяца
   */
  setMonth(e) {
    e.stopPropagation();
    const target = e.target.closest("[data-month]");
    if (!target || target.classList.contains("-disabled-")) return;
    this.date.setMonth(+target.dataset.month);
    this.view = "day";
    this.emit("setDate");
    this.emit("changeView");
  }

  /**
   * Выбор года
   */
  setYear(e) {
    e.stopPropagation();
    const target = e.target.closest("[data-year]");
    if (!target || target.classList.contains("-disabled-")) return;
    this.date.setFullYear(+target.dataset.year);
    this.view = "month";
    this.emit("setDate");
    this.emit("changeView");
  }

  /**
   * Установка текущей даты
   */
  setNowDate() {
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();
    let date = now.getDate();
    if (this.minDate && now < this.minDate.date) {
      year = this.minDate.year;
      month = this.minDate.month;
      date = this.minDate.day;
    } else if (this.maxDate && now > this.maxDate.date) {
      year = this.maxDate.year;
      month = this.maxDate.month;
      date = this.maxDate.day;
    }
    this.date = new Date(year, month, date);
    this.emit("setDate");
    this.emit("changeView");
    this.$root.classList.remove(this.classShow);
  }

  /**
   * Назад
   */
  prevView() {
    switch (this.view) {
      case "year":
        this.shiftYear--;
        this.yearToHtml();
        break;
      case "month":
        this.date.setFullYear(--this.yearCurrent);
        this.emit("changeView");
        break;
      case "day":
        if (this.monthCurrent === 0) {
          this.date.setMonth(11);
          this.date.setFullYear(--this.yearCurrent);
        } else {
          this.date.setMonth(this.monthCurrent - 1);
        }
        this.emit("changeView");
        break;
    }
  }

  /**
   * Вперёд
   */
  nextView() {
    switch (this.view) {
      case "year":
        this.shiftYear++;
        this.yearToHtml();
        break;
      case "month":
        this.date.setFullYear(++this.yearCurrent);
        this.emit("changeView");
        break;
      case "day":
        if (this.monthCurrent === 11) {
          this.date.setMonth(0);
          this.date.setFullYear(++this.yearCurrent);
        } else {
          this.date.setMonth(this.monthCurrent + 1);
        }
        this.emit("changeView");
        break;
    }
  }

  /**
   * Отображение выбранной даты в input
   */
  setDateInput() {
    let isInit = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    this.clampDate(); // Перестраховка

    this.dateInput = new Date(this.date);
    this.$input.value = moment(this.date).format("YYYY-MM-DD"); // или ISO: "YYYY-MM-DD"

    if (!isInit) {
      this.$input.dispatchEvent(new Event("change", {
        bubbles: true
      }));
    }
    this.shiftYear = 0;
  }

  /**
   * Переключение активного вида
   */
  switchView() {
    [this.$dpDay, this.$dpMonth, this.$dpYear].forEach(el => el.classList.remove("active"));
    switch (this.view) {
      case "day":
        this.$dpDay.classList.add("active");
        this.$dpNowDate.innerHTML = this.messages.today;
        break;
      case "month":
        this.$dpMonth.classList.add("active");
        this.$dpNowDate.innerHTML = this.messages.current;
        break;
      case "year":
        this.$dpYear.classList.add("active");
        this.$dpNowDate.innerHTML = this.messages.current;
        break;
    }
  }

  /**
   * Маска ввода (DD.MM.YYYY)
   */
  dateInputMask(e) {
    const key = e.key;
    if (!/[0-9]/.test(key) && key !== this.separator) {
      e.preventDefault();
      return;
    }
    const value = this.$input.value;
    if (value.length === 2 || value.length === 5) {
      this.$input.value += this.separator;
    }
  }

  /**
   * Ручной ввод даты
   */
  inputSetDate() {
    if (this.$input.value.length === 10) {
      const date = this.parseInputValue();
      if (date) {
        this.date = date;
        this.clampDate();
        this.emit("setDate");
        this.emit("changeView");
      }
    }
  }

  /**
   * Показ пикера
   */
  showDate() {
    if (!this.$root.classList.contains(this.classShow)) {
      this.$root.classList.add(this.classShow);
      if (!this.checkDateInputFreeUnderspace()) {
        this.$root.classList.add(`${this.classShow}-above`);
      }
      this.$input.dispatchEvent(new Event("datepicker-show"));
    }
  }

  /**
   * Проверка места под пикером
   * @returns {boolean}
   */
  checkDateInputFreeUnderspace() {
    const rect = this.$input.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const pickerHeight = this.$datePickerRoot?.clientHeight || 300;
    return spaceBelow > pickerHeight + 10;
  }

  /**
   * Скрытие пикера при клике вне
   */
  hideDate = e => {
    const inside = e.target.closest(`#root_datepicker-${this.indexDatePicker}`);
    if (!inside) {
      this.$root.classList.remove(this.classShow, `${this.classShow}-above`);
    }
  };

  /**
   * Подписка на события
   */
  addListener() {
    this.$dpDay.addEventListener("click", this.setDay.bind(this));
    this.$dpMonth.addEventListener("click", this.setMonth.bind(this));
    this.$dpYear.addEventListener("click", this.setYear.bind(this));
    this.$dpSetView.addEventListener("click", this.setView.bind(this));
    this.$dpNowDate.addEventListener("click", this.setNowDate.bind(this));
    this.$dpPrev.addEventListener("click", this.prevView.bind(this));
    this.$dpNext.addEventListener("click", this.nextView.bind(this));
    this.$input.addEventListener("input", this.inputSetDate.bind(this));
    this.$input.addEventListener("keypress", this.dateInputMask.bind(this));
    this.$input.addEventListener("click", this.showDate.bind(this));

    // Закрытие при клике вне
    const overlay = this.$datePickerRoot.closest(".form-overlay") || document.body;
    overlay.addEventListener("click", this.hideDate);
  }

  /**
   * Инициализация
   */
  init() {
    // Создание контейнера
    this.$datePickerRoot = document.createElement("div");
    this.$datePickerRoot.classList.add("qform-field__block_box__field__view");
    this.$datePickerRoot.innerHTML = this.toHtml();
    this.$box.appendChild(this.$datePickerRoot);

    // Привязка элементов
    this.$dpNavTop = document.getElementById(`navTop_${this.indexDatePicker}`);
    this.topNavToHtml();
    this.$dpDay = document.getElementById(`datepickerDay_${this.indexDatePicker}`);
    this.$dpMonth = document.getElementById(`datepickerMonths_${this.indexDatePicker}`);
    this.$dpYear = document.getElementById(`datepickerYears_${this.indexDatePicker}`);
    this.$dpSetView = document.getElementById(`setView_${this.indexDatePicker}`);
    this.$dpNowDate = document.getElementById(`setNowDate_${this.indexDatePicker}`);
    this.$dpPrev = document.getElementById(`dpPrev_${this.indexDatePicker}`);
    this.$dpNext = document.getElementById(`dpNext_${this.indexDatePicker}`);

    // Первичный рендер
    this.dayToHtml();
    this.monthToHtml();
    this.yearToHtml();
    this.switchView();
    this.setDefaultDate && this.setDateInput(true);

    // Подписки
    this.subscribe("changeView", this.updateData.bind(this));
    this.subscribe("changeView", this.switchView.bind(this));
    this.subscribe("setDate", this.setDateInput.bind(this));

    // Слушатели
    this.addListener();
  }

  // --- Event System ---
  emit(event) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    if (Array.isArray(this.listeners[event])) {
      this.listeners[event].forEach(fn => fn(...args));
    }
  }
  subscribe(event, fn) {
    this.listeners[event] = this.listeners[event] || [];
    this.listeners[event].push(fn);
    return () => {
      this.listeners[event] = this.listeners[event].filter(f => f !== fn);
    };
  }
}